import React, { useEffect, useMemo, useState } from "react";
import { FileText, Search, Download, ShieldCheck, HandHeart, Hammer, Filter } from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

/**
 * /admin → "Legal records" tab
 *
 * Single searchable table of every signed hold-harmless acknowledgment
 * (volunteers + contractors combined). Each row links to the inline PDF
 * download for audits or insurance reviews.
 */
const fmtDate = (s) => {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return s;
    }
};

export const LegalRecordsPanel = () => {
    const [records, setRecords] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [kindFilter, setKindFilter] = useState("all"); // all | volunteer | contractor

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q.trim()) params.q = q.trim();
            if (kindFilter !== "all") params.kind = kindFilter;
            const { data } = await api.get("/admin/legal-records", { params });
            setRecords(data.records || []);
            setCount(data.count || 0);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load records");
        } finally {
            setLoading(false);
        }
    };

    // Live filter on kind; debounced search via Enter or button
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kindFilter]);

    const counts = useMemo(() => {
        const v = records.filter((r) => r.kind === "volunteer").length;
        const c = records.filter((r) => r.kind === "contractor").length;
        return { volunteers: v, contractors: c };
    }, [records]);

    const downloadPdf = async (record) => {
        // Use fetch with cookies so the PDF endpoint authenticates via the same session
        try {
            const res = await fetch(`${API}${record.pdf_url.replace(/^\/api/, "")}`, {
                credentials: "include",
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safeName = (record.subject || record.kind).replace(/[^a-zA-Z0-9_\- ]/g, "_").replace(/\s+/g, "_");
            a.download = `reddog-${record.kind}-hold-harmless-${safeName}-${(record.id || "").slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Could not download PDF — please make sure you're signed in.");
        }
    };

    return (
        <div data-testid="admin-legal-records-panel" className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Legal records</h2>
                    <p className="text-xs text-[#4A5568] mt-1 max-w-2xl">
                        Every signed volunteer waiver and contractor hold-harmless on file, in one place. Each row links to the printable PDF
                        that was emailed to the participant and the board at the time of acknowledgment.
                    </p>
                </div>
                <div className="flex gap-3 text-xs text-[#4A5568]">
                    <div className="bg-white border border-black/5 rounded-xl px-4 py-2.5" data-testid="legal-records-count-total">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Total</p>
                        <p className="font-heading text-xl text-[#1C252E] font-medium">{count}</p>
                    </div>
                    <div className="bg-white border border-black/5 rounded-xl px-4 py-2.5" data-testid="legal-records-count-volunteers">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] inline-flex items-center gap-1"><HandHeart className="w-3 h-3" />Volunteers</p>
                        <p className="font-heading text-xl text-[#1C252E] font-medium">{counts.volunteers}</p>
                    </div>
                    <div className="bg-white border border-black/5 rounded-xl px-4 py-2.5" data-testid="legal-records-count-contractors">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] inline-flex items-center gap-1"><Hammer className="w-3 h-3" />Contractors</p>
                        <p className="font-heading text-xl text-[#1C252E] font-medium">{counts.contractors}</p>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <div className="flex-1 flex items-center gap-2 bg-[#FDFBF7] rounded-xl px-3 border border-black/5">
                    <Search className="w-4 h-4 text-[#4A5568]" strokeWidth={1.75} />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search by name, email, business, trade…"
                        data-testid="legal-records-search"
                        className="flex-1 bg-transparent py-2.5 text-sm outline-none placeholder:text-[#9CA3AF]"
                    />
                </div>
                <div className="flex items-center gap-1 bg-[#FDFBF7] rounded-xl p-1 border border-black/5" role="tablist" aria-label="Filter by kind">
                    {[
                        { v: "all", label: "All", icon: Filter },
                        { v: "volunteer", label: "Volunteers", icon: HandHeart },
                        { v: "contractor", label: "Contractors", icon: Hammer },
                    ].map((opt) => (
                        <button
                            key={opt.v}
                            onClick={() => setKindFilter(opt.v)}
                            data-testid={`legal-records-filter-${opt.v}`}
                            className={`px-3 py-2 text-xs font-medium rounded-lg inline-flex items-center gap-1.5 transition-colors ${
                                kindFilter === opt.v ? "bg-[#1C252E] text-white" : "text-[#4A5568] hover:text-[#1C252E]"
                            }`}
                        >
                            <opt.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                            {opt.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={load}
                    data-testid="legal-records-search-btn"
                    className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-xl px-5 py-2.5 text-sm font-medium"
                >
                    Search
                </button>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <table className="w-full text-sm" data-testid="legal-records-table">
                    <thead className="bg-[#F4F1EA] text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">
                        <tr>
                            <th className="px-5 py-3 text-left">Type</th>
                            <th className="px-5 py-3 text-left">Subject</th>
                            <th className="px-5 py-3 text-left">Email</th>
                            <th className="px-5 py-3 text-left">Acknowledged</th>
                            <th className="px-5 py-3 text-left">Status</th>
                            <th className="px-5 py-3 text-right">PDF</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {loading && (
                            <tr><td colSpan={6} className="px-5 py-10 text-center text-[#4A5568]">Loading…</td></tr>
                        )}
                        {!loading && records.length === 0 && (
                            <tr><td colSpan={6} className="px-5 py-10 text-center text-[#4A5568]" data-testid="legal-records-empty">
                                No signed acknowledgments match.
                            </td></tr>
                        )}
                        {!loading && records.map((r) => (
                            <tr key={`${r.kind}-${r.id}`} data-testid={`legal-records-row-${r.id}`} className="hover:bg-[#FDFBF7]">
                                <td className="px-5 py-3">
                                    {r.kind === "volunteer" ? (
                                        <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full">
                                            <HandHeart className="w-3 h-3" strokeWidth={2} /> Volunteer
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full">
                                            <Hammer className="w-3 h-3" strokeWidth={2} /> Contractor
                                        </span>
                                    )}
                                </td>
                                <td className="px-5 py-3">
                                    <p className="font-medium text-[#1C252E]">{r.subject || "—"}</p>
                                    {r.secondary && <p className="text-[11px] text-[#4A5568]">{r.secondary}</p>}
                                    {r.contact_name && r.kind === "contractor" && <p className="text-[11px] text-[#4A5568]">Contact: {r.contact_name}</p>}
                                </td>
                                <td className="px-5 py-3 text-[#4A5568]">{r.email || "—"}</td>
                                <td className="px-5 py-3 text-[#4A5568]">{fmtDate(r.accepted_at)}</td>
                                <td className="px-5 py-3">
                                    <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F4F1EA] text-[#4A5568] px-2 py-1 rounded-full">
                                        {r.status || "—"}
                                    </span>
                                    {r.promoted_to_contractor_id && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-green-700">
                                            <ShieldCheck className="w-3 h-3" /> Vetted
                                        </span>
                                    )}
                                </td>
                                <td className="px-5 py-3 text-right">
                                    <button
                                        onClick={() => downloadPdf(r)}
                                        data-testid={`legal-records-pdf-${r.id}`}
                                        className="inline-flex items-center gap-1.5 text-[#E53935] hover:text-[#D32F2F] text-xs font-medium"
                                    >
                                        <Download className="w-3.5 h-3.5" strokeWidth={2} /> PDF
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="text-[11px] text-[#9CA3AF]" data-testid="legal-records-disclosure">
                <FileText className="w-3 h-3 inline mr-1" />
                Records shown here are signed acknowledgments only. The underlying volunteer and contractor application data
                remains in the respective admin tabs. PDFs include the timestamp, identity, and full clause text exactly as
                presented at submission.
            </p>
        </div>
    );
};

export default LegalRecordsPanel;
